import React, { useEffect, useState } from 'react';
import { Bell } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { esApp } from '../../utils/plataforma';
import { recordarAjusteDispositivo } from '../../utils/preferencias';
import {
  EstadoNotificaciones, estadoNotificaciones, activarNotificaciones, desactivarNotificaciones, enviarPrueba,
} from '../../utils/notificaciones';

/**
 * Interruptor de las notificaciones (solo en la app). El permiso de iOS se
 * pide aquí, cuando la persona lo activa, y no al abrir la app: Apple lo
 * prefiere así y la gente acepta más cuando sabe para qué es.
 */
export const NotificacionesPush: React.FC = () => {
  const { user } = useAuth();
  const [estado, setEstado] = useState<EstadoNotificaciones>('no-disponible');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  const [prueba, setPrueba] = useState<{ ok: boolean; mensaje: string } | null>(null);

  useEffect(() => {
    if (esApp) estadoNotificaciones().then(setEstado).catch(() => setEstado('no-disponible'));
  }, []);

  if (!esApp || estado === 'no-disponible' || !user) return null;
  const activo = estado === 'activadas';

  const cambiar = async () => {
    setError(''); setPrueba(null); setOcupado(true);
    try {
      if (activo) {
        await desactivarNotificaciones(user.uid);
        setEstado('desactivadas');
        recordarAjusteDispositivo({ notificaciones: false });
      } else {
        const nuevo = await activarNotificaciones(user.uid);
        setEstado(nuevo);
        if (nuevo === 'activadas') recordarAjusteDispositivo({ notificaciones: true });
      }
    } catch {
      setError('No se han podido activar. Inténtalo de nuevo en un rato.');
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div className="border-t border-gray-100 dark:border-gray-700 pt-5">
      <div className="flex items-center gap-3">
        <Bell className="w-5 h-5 text-gray-500 dark:text-gray-400 shrink-0" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Notificaciones</h3>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Te avisamos cuando tengas pendiente pagar tu parte en un plan de A medias.
          </p>
        </div>
        <button role="switch" aria-checked={activo} onClick={cambiar} disabled={ocupado || estado === 'denegadas'}
          className={`relative w-12 h-7 rounded-full transition shrink-0 disabled:opacity-50 ${
            activo ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}>
          <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${
            activo ? 'translate-x-5' : ''}`} />
        </button>
      </div>
      {estado === 'denegadas' && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
          Las notificaciones están bloqueadas para esta app. Actívalas en Ajustes de iOS → Notificaciones.
        </p>
      )}
      {activo && (
        <div className="mt-2">
          <button onClick={async () => { setOcupado(true); setPrueba(await enviarPrueba()); setOcupado(false); }}
            disabled={ocupado} className="text-xs text-blue-600 dark:text-blue-400 disabled:opacity-50">
            Enviar una de prueba
          </button>
          {prueba && (
            <p className={`text-xs mt-1 ${prueba.ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {prueba.mensaje}
            </p>
          )}
        </div>
      )}
      {error && <p className="text-xs text-red-600 dark:text-red-400 mt-2">{error}</p>}
    </div>
  );
};
